import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from 'auth/Auth';
// Aliased: the hook has its own applyConditions (the drawer's Apply action)
import { applyConditions as matchRows, normalizeCondition, normalizeObjectType } from 'utils/filterMatch';

/**
 * Generic filter / saved-filter / search logic for Kanban (or any card) views.
 *
 *   const kanban = useKanbanFilters({
 *     objectType: 'lead',            // same objectType you pass to DataTable
 *     rows: filteredLeads,           // rows to filter
 *     searchFields: ['name', ...],   // keep this array outside the component (stable identity)
 *     enabled: viewMode === 'kanban' // skip all API calls while the view is hidden
 *   });
 *   kanban.visibleRows  -> filtered + searched rows
 *
 * It talks to the same endpoints as DataTable and uses the same localStorage
 * key, so a filter applied in List view is still applied in Kanban view.
 */

const EMPTY_CONDITION = { field: '', operator: '', value: '' };
const NO_VALUE_OPERATORS = ['is_empty', 'is_not_empty'];

const isBlank = (v) => v === null || v === undefined || v === '';
export const needsValue = (operator) => !NO_VALUE_OPERATORS.includes(operator);

const readStored = (key) => {
    if (!key) return {};
    try {
        return JSON.parse(localStorage.getItem(key)) || {};
    } catch (e) {
        return {};
    }
};

export default function useKanbanFilters({
                                             objectType: objectTypeProp,
                                             rows = [],
                                             searchFields,
                                             enabled = true,
                                             onFilterApply,
                                             onFilterClear,
                                         }) {
    const { axiosInstance } = useAuth();
    const objectType = normalizeObjectType(objectTypeProp);
    const storageKey = objectType ? `dataTable_filter_${objectType}` : null;
    const [stored] = useState(() => readStored(storageKey));

    /* ------------------------------ core state ------------------------------ */
    const [appliedFilters, setAppliedFilters] = useState(stored.appliedFilters || null);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const pendingSelectedIdRef = useRef(stored.selectedFilterId || null);
    const [search, setSearch] = useState('');
    const [searchResetKey, setSearchResetKey] = useState(0); // bumps on every clearAll

    const [savedFilters, setSavedFilters] = useState([]);
    const [loadingFilters, setLoadingFilters] = useState(false);

    /* ------------------------------ drawer state ----------------------------- */
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [conditions, setConditions] = useState([{ ...EMPTY_CONDITION }]);
    const [logicOperator, setLogicOperator] = useState('AND');
    const [editingFilter, setEditingFilter] = useState(null);

    /* ------------------------------ metadata -------------------------------- */
    const [availableFields, setAvailableFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [operatorsCache, setOperatorsCache] = useState({});
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [referenceData, setReferenceData] = useState({});
    const [loadingRef, setLoadingRef] = useState({});
    const [fieldsSettled, setFieldsSettled] = useState(false);

    const fieldsRequestedRef = useRef(false);
    const usersRequestedRef = useRef(false);
    const operatorsRequestedRef = useRef(new Set());
    const referenceBaseRequestedRef = useRef(new Set());

    /* ------------------------------ dialogs --------------------------------- */
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [filterDescription, setFilterDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [notice, setNotice] = useState({ open: false, message: '', severity: 'info' });

    const notify = (message, severity = 'info') => setNotice({ open: true, message, severity });
    const closeNotice = () => setNotice((n) => ({ ...n, open: false }));

    const fieldMap = useMemo(
        () => Object.fromEntries(availableFields.map((f) => [f.name, f])),
        [availableFields]
    );
    const getField = useCallback((name) => fieldMap[name], [fieldMap]);

    /* ------------------------------ persistence ----------------------------- */
    const persist = useCallback(
        (applied, selectedId) => {
            if (!storageKey) return;
            try {
                if (!applied) {
                    localStorage.removeItem(storageKey);
                    return;
                }
                localStorage.setItem(
                    storageKey,
                    JSON.stringify({
                        appliedFilters: applied,
                        selectedFilterId: selectedId || null,
                        activeFiltersCount: applied.conditions.length,
                    })
                );
            } catch (e) {
                console.error('Error saving filters to localStorage:', e);
            }
        },
        [storageKey]
    );

    /* ------------------------------ API: saved filters ---------------------- */
    const fetchSavedFilters = useCallback(async () => {
        if (!objectType || !axiosInstance) return;
        setLoadingFilters(true);
        try {
            const response = await axiosInstance.get(`/filters/by-object/${objectType}`);
            setSavedFilters(response.data || []);
        } catch (error) {
            console.error('Error fetching saved filters:', error);
            setSavedFilters([]);
        } finally {
            setLoadingFilters(false);
        }
    }, [objectType, axiosInstance]);

    useEffect(() => {
        if (enabled) fetchSavedFilters();
    }, [enabled, fetchSavedFilters]);

    // Re-select the saved filter that was active before a page reload / view switch
    useEffect(() => {
        if (!pendingSelectedIdRef.current || selectedFilter || savedFilters.length === 0) return;
        const match = savedFilters.find((f) => f.id === pendingSelectedIdRef.current);
        if (match) setSelectedFilter(match);
        pendingSelectedIdRef.current = null;
    }, [savedFilters, selectedFilter]);

    /* ------------------------------ API: fields ----------------------------- */
    const fetchAvailableFields = useCallback(async () => {
        if (!objectType || !axiosInstance || fieldsRequestedRef.current) return;
        fieldsRequestedRef.current = true;
        setLoadingFields(true);
        try {
            let response;
            try {
                response = await axiosInstance.get(`/filters/metadata/fields/${objectType}`);
            } catch (err) {
                // The metadata router has no prefix: it serves /fields/{object}
                response = await axiosInstance.get(`/fields/${objectType}`);
            }
            const data = response.data;
            if (Array.isArray(data)) {
                setAvailableFields(data);
            } else if (data && typeof data === 'object') {
                setAvailableFields(
                    Object.entries(data).map(([name, def]) => ({
                        name,
                        label: def.label || name,
                        type: def.type || 'text',
                        options: def.options || [],
                        reference_to: def.reference_to,
                        required: def.required || false,
                        is_custom: def.is_custom || false,
                    }))
                );
            } else {
                setAvailableFields([]);
            }
        } catch (error) {
            console.warn('Could not load filter fields:', error?.message);
            fieldsRequestedRef.current = false;
            setAvailableFields([]);
        } finally {
            setLoadingFields(false);
            setFieldsSettled(true);   // success or failure, we stop waiting
        }
    }, [objectType, axiosInstance]);

    useEffect(() => {
        if (!enabled) return;
        const s = readStored(storageKey);
        setAppliedFilters(s.appliedFilters || null);
        setSelectedFilter(null);
        pendingSelectedIdRef.current = s.selectedFilterId || null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    // Fields are needed when the drawer opens, and also to evaluate a restored filter
    useEffect(() => {
        if (enabled && (drawerOpen || appliedFilters)) fetchAvailableFields();
    }, [enabled, drawerOpen, appliedFilters, fetchAvailableFields]);

    /* ------------------------------ API: operators -------------------------- */
    const fetchOperatorsForType = useCallback(
        async (fieldType) => {
            if (!fieldType || !axiosInstance || operatorsRequestedRef.current.has(fieldType)) return;
            operatorsRequestedRef.current.add(fieldType);
            const urls = [
                `/filters/metadata/operators/${fieldType}`,
                `/operators/${fieldType}`,
            ];
            for (const url of urls) {
                try {
                    const { data } = await axiosInstance.get(url);
                    if (Array.isArray(data)) {
                        setOperatorsCache((prev) => ({ ...prev, [fieldType]: data }));
                        return;
                    }
                } catch (e) {
                    /* try next endpoint */
                }
            }
            operatorsRequestedRef.current.delete(fieldType);
        },
        [axiosInstance]
    );

    useEffect(() => {
        if (!drawerOpen || availableFields.length === 0) return;
        new Set(availableFields.map((f) => f.type || 'text')).forEach(fetchOperatorsForType);
    }, [drawerOpen, availableFields, fetchOperatorsForType]);

    const getOperatorsForField = (fieldName) => operatorsCache[fieldMap[fieldName]?.type || 'text'] || [];

    /* ------------------------------ API: users / references ----------------- */
    const fetchUsers = useCallback(async () => {
        if (!axiosInstance || usersRequestedRef.current) return;
        usersRequestedRef.current = true;
        setLoadingUsers(true);
        try {
            const { data } = await axiosInstance.get('/users/');
            setUsers(
                (data || []).map((u) => ({
                    id: u.id,
                    label: u.fullname || u.username || u.name || 'Unknown User',
                }))
            );
        } catch (error) {
            console.error('Error fetching users:', error);
            usersRequestedRef.current = false;
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }, [axiosInstance]);

    const fetchReferenceData = useCallback(
        async (referenceType, query = '', force = false) => {
            if (!referenceType || !axiosInstance) return;
            if (!query && !force && referenceBaseRequestedRef.current.has(referenceType)) return;
            if (!query) referenceBaseRequestedRef.current.add(referenceType);

            setLoadingRef((p) => ({ ...p, [referenceType]: true }));
            try {
                const params = query ? { search: query, limit: 50 } : { limit: 100 };
                const { data } = await axiosInstance.get(`/reference-data/${referenceType}`, { params });
                const items = (data || [])
                    .map((item) => ({
                        id: item.value !== undefined ? item.value : item.id !== undefined ? item.id : null,
                        label: item.label !== undefined ? item.label : item.name !== undefined ? item.name : 'Unknown',
                    }))
                    .filter((item) => item.id !== null);
                setReferenceData((p) => ({ ...p, [referenceType]: items }));
            } catch (error) {
                console.error(`Error fetching reference data for ${referenceType}:`, error);
                if (!query) referenceBaseRequestedRef.current.delete(referenceType);
                setReferenceData((p) => ({ ...p, [referenceType]: [] }));
            } finally {
                setLoadingRef((p) => ({ ...p, [referenceType]: false }));
            }
        },
        [axiosInstance]
    );

    // Load option lists for any reference field used by a condition in the drawer
    useEffect(() => {
        if (!drawerOpen || availableFields.length === 0) return;
        const types = new Set(
            conditions
                .map((c) => fieldMap[c.field])
                .filter((f) => f?.type === 'reference' && f.reference_to)
                .map((f) => f.reference_to)
        );
        types.forEach((t) => (t === 'users' ? fetchUsers() : fetchReferenceData(t)));
    }, [drawerOpen, conditions, availableFields, fieldMap, fetchUsers, fetchReferenceData]);

    /* ------------------------------ panel actions --------------------------- */
    const resetPanel = () => {
        setConditions([{ ...EMPTY_CONDITION }]);
        setLogicOperator('AND');
        setFilterName('');
        setFilterDescription('');
        setEditingFilter(null);
    };

    const openDrawer = () => {
        resetPanel(); // like List view: the panel always starts a NEW filter
        setDrawerOpen(true);
    };
    const closeDrawer = () => setDrawerOpen(false);
    const cancelDrawer = () => {
        resetPanel();
        setDrawerOpen(false);
    };

    const addCondition = () => setConditions((prev) => [...prev, { ...EMPTY_CONDITION }]);
    const removeCondition = (index) =>
        setConditions((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

    const updateCondition = (index, key, value) =>
        setConditions((prev) =>
            prev.map((c, i) => {
                if (i !== index) return c;
                if (key === 'field') return { field: value, operator: '', value: '' };
                if (key === 'operator' && !needsValue(value)) return { ...c, operator: value, value: '' };
                return { ...c, [key]: value };
            })
        );

    const getValidConditions = () =>
        conditions.filter((c) => c.field && c.operator && (!needsValue(c.operator) || !isBlank(c.value)));

    /* ------------------------------ apply / clear --------------------------- */
    const applyConditions = () => {
        const valid = getValidConditions();
        if (valid.length === 0) {
            notify('Pick a field, an operator and a value to apply a filter.', 'info');
            return;
        }
        const applied = { conditions: valid, logic_operator: logicOperator };
        setAppliedFilters(applied);
        setSelectedFilter(null);
        persist(applied, null);
        onFilterApply?.(applied);
        closeDrawer();
    };

    const selectSavedFilter = (filter) => {
        setSelectedFilter(filter);
        if (filter.conditions?.length > 0) {
            const applied = { conditions: filter.conditions, logic_operator: filter.logic_operator || 'AND' };
            setAppliedFilters(applied);
            persist(applied, filter.id);
        } else {
            setAppliedFilters(null);
            persist(null);
        }
        onFilterApply?.(filter);
    };

    const clearFilter = () => {
        setSelectedFilter(null);
        setAppliedFilters(null);
        pendingSelectedIdRef.current = null;
        persist(null);
        onFilterClear?.();
    };

    const clearAll = () => {
        clearFilter();
        setSearch('');
        setSearchResetKey((k) => k + 1);
    };

    /* ------------------------------ save / edit / delete -------------------- */
    const openSaveDialog = () => {
        if (getValidConditions().length === 0) {
            notify('Add at least one complete filter condition before saving.', 'info');
            return;
        }
        setSaveDialogOpen(true);
    };
    const closeSaveDialog = () => setSaveDialogOpen(false);

    const submitFilter = async () => {
        const name = filterName.trim();
        if (!name) {
            notify('Please enter a filter name.', 'info');
            return;
        }
        const valid = getValidConditions();
        if (valid.length === 0) {
            notify('Add at least one complete filter condition.', 'info');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name,
                description: filterDescription.trim() || null,
                object_type: objectType,
                conditions: valid,
                logic_operator: logicOperator,
                is_default: editingFilter?.is_default || false,
            };
            const { data } = editingFilter
                ? await axiosInstance.put(`/filters/${editingFilter.id}`, payload)
                : await axiosInstance.post('/filters/', payload);

            const applied = {
                conditions: data?.conditions || valid,
                logic_operator: data?.logic_operator || logicOperator,
            };
            fetchSavedFilters();
            setAppliedFilters(applied);
            setSelectedFilter(data || null);
            persist(applied, data?.id);

            const wasEditing = Boolean(editingFilter);
            setSaveDialogOpen(false);
            resetPanel();
            setDrawerOpen(false);
            notify(wasEditing ? 'Filter updated and applied.' : 'Filter saved and applied.', 'success');
        } catch (error) {
            console.error('Error saving filter:', error);
            notify('Failed to save filter. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const editSavedFilter = (filter) => {
        setEditingFilter(filter);
        setConditions(
            (filter.conditions?.length ? filter.conditions : [EMPTY_CONDITION]).map((raw) => {
                // Old saved filters may still use renamed fields ('type' -> 'deal_type')
                const c = normalizeCondition(raw, objectType);
                return {
                field: c.field,
                operator: c.operator,
                value: c.value ?? '',
                };
            })
        );
        setLogicOperator(filter.logic_operator || 'AND');
        setFilterName(filter.name || '');
        setFilterDescription(filter.description || '');
        setDrawerOpen(true);
    };

    const requestDelete = (filter) => setDeleteTarget(filter);
    const cancelDelete = () => setDeleteTarget(null);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`/filters/${deleteTarget.id}`);
            if (selectedFilter?.id === deleteTarget.id) clearFilter();
            fetchSavedFilters();
            setDeleteTarget(null);
        } catch (error) {
            console.error('Error deleting filter:', error);
            notify('Failed to delete filter. Please try again.', 'error');
        } finally {
            setDeleting(false);
        }
    };

    /* ------------------------------ the actual filtering -------------------- */
    const visibleRows = useMemo(() => {
        let result = matchRows(rows || [], appliedFilters, fieldMap, objectType);

        const term = search.trim().toLowerCase();
        if (term) {
            result = result.filter((row) => {
                const keys = searchFields?.length ? searchFields : Object.keys(row);
                return keys.some((k) => {
                    const v = row[k];
                    return v !== null && v !== undefined && typeof v !== 'object' && String(v).toLowerCase().includes(term);
                });
            });
        }
        return result;
    }, [rows, appliedFilters, fieldMap, objectType, search, searchFields]);

    const activeCount = appliedFilters?.conditions?.length || 0;
    // A restored filter can't be evaluated correctly until field types are known
    const filtersLoading = enabled && activeCount > 0 && !fieldsSettled;

    return {
        objectType,
        // result
        visibleRows,
        filtersLoading,
        totalCount: (rows || []).length,
        isFiltering: activeCount > 0 || search.trim() !== '',
        activeCount,
        // search
        search,
        setSearch,
        searchResetKey,
        // applied / saved filters
        appliedFilters,
        selectedFilter,
        savedFilters,
        loadingFilters,
        selectSavedFilter,
        clearFilter,
        clearAll,
        // drawer
        drawerOpen,
        openDrawer,
        closeDrawer,
        cancelDrawer,
        editingFilter,
        conditions,
        logicOperator,
        setLogicOperator,
        addCondition,
        removeCondition,
        updateCondition,
        applyConditions,
        // metadata
        availableFields,
        loadingFields,
        getField,
        getOperatorsForField,
        users,
        loadingUsers,
        referenceData,
        loadingRef,
        fetchReferenceData,
        // save dialog
        saveDialogOpen,
        openSaveDialog,
        closeSaveDialog,
        filterName,
        setFilterName,
        filterDescription,
        setFilterDescription,
        submitFilter,
        saving,
        editSavedFilter,
        // delete dialog
        deleteTarget,
        requestDelete,
        cancelDelete,
        confirmDelete,
        deleting,
        // feedback
        notice,
        closeNotice,
    };
}